import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmType } from 'APIPackage/CatalogsModule/interfaces/IKnmType';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { getCatalogKnmTypesAll } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type KnmTypeForm = Omit<IKnmType, 'id'>;

interface IKnmTypeEditFormProps {
  onBack: () => void;
}

interface IKnmTypeEditFormStoreProps {
  knmTypes: IKnmType[];
}

const KnmTypeEditFormWithoutStore = ({ onBack, knmTypes }: IKnmTypeEditFormProps & IKnmTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmType, setSelectedKnmType] = useState<IKnmType | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmType = knmTypes.find(k => k.id.toString() === id);

      setSelectedKnmType(prev => (prev?.id === knmType?.id ? undefined : knmType));
    },
    [knmTypes],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_TYPES_ALL));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
      weight: 0,
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmTypeForm) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_TYPES_ALL }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { ...values, id: selectedKnmType!.id },
            catalogName: CommonCatalogsNamesEnum.KNM_TYPES_ALL,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmTypeForm>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Вес" color="black">
            <TextInputField min={1} max={999999999} type="number" name="weight" />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmTypeForm> onSubmit={onSubmit} initialValues={selectedKnmType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Типы КНМ</span>
        {knmTypes &&
          knmTypes.map((knmType: IKnmType) => (
            <button
              key={knmType.id}
              className={selectedKnmType?.id === knmType.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmType.id}
              onClick={onRowClick}
              type="button"
            >
              {knmType.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmTypeEditFormStoreProps => ({
  knmTypes: getCatalogKnmTypesAll(state),
});

export const KnmTypeEditForm = connect(mapStateToProps)(KnmTypeEditFormWithoutStore);
